import { ApiError } from './api'

export interface BlockedInfo {
  code: string | null
  message: string
  retryable: boolean
  // Set only for messages whose resolution requires Raven, not the
  // reader's own company -- see RAVEN_LINK below. Undefined for every
  // customer-side-actionable message.
  link?: { href: string; label: string }
}

interface KnownMessage {
  message: string
  link?: { href: string; label: string }
}

// Every blocked reason ultimately has exactly one party who can actually
// fix it -- the reader's own organization's ScopeAssistant administrator, or Raven
// Automation. Getting that wrong sends someone to their own IT department
// for a problem their IT department has no way to fix (or the reverse).
// When adding a new code here, ask which one applies before writing the
// message, not after.
const RAVEN_LINK = { href: 'https://ravenautomation.com', label: 'ravenautomation.com' }

// Codes with their own actionable message, because each implies a
// different next step for the reader. Any ApiError code NOT in this map --
// including one the API starts returning tomorrow that nobody updates this
// file for -- falls through to GENERIC_MESSAGE below rather than being
// silently treated as "fine." See useCurrentUser.ts: this function is only
// ever reached for an authMe rejection that isn't one of the five codes
// with their own dedicated authStatus/page (PENDING_APPROVAL,
// NO_INVITE_FOUND, ACCESS_DENIED, EXTERNAL_TENANT, TENANT_NOT_PROVISIONED
// -- the last of which is deliberately NOT in this map: it now has its own
// provisioning screen, ProvisionTenant.tsx, and is intercepted in
// useCurrentUser.ts before resolveBlockedInfo is ever called. If it shows
// up in this map again, that interception has regressed).
const KNOWN_MESSAGES: Record<string, KnownMessage> = {
  // No tenant registry row exists for this Microsoft account's Entra
  // tenant at all -- there is no ScopeAssistant admin anywhere at the reader's own
  // company to contact, because their company was never set up as a
  // ScopeAssistant customer in the first place (or the wrong Microsoft account was
  // used). Written for someone who may have no idea what ScopeAssistant is or why
  // they landed on this page -- only Raven Automation, who operates
  // ScopeAssistant, can do anything about this. Covers both a self-service fix
  // (wrong credentials -- try a different account) and the two cases only
  // Raven can act on (a genuine setup error, or a prospect who isn't a
  // customer yet). The message names Raven Automation but does not spell
  // out the URL inline -- RAVEN_LINK below is rendered as its own element
  // on the page (AccessBlockedPage.tsx), so the two would otherwise
  // duplicate each other.
  UNKNOWN_TENANT: {
    message:
      "We couldn't find a ScopeAssistant workspace for your organization. Please confirm you're using the correct credentials. If you believe you're getting this message in error or are interested in purchasing ScopeAssistant for your business, please contact us.",
    link: RAVEN_LINK,
  },
  // The tenant's schema already exists but has no users yet, and this
  // caller isn't the specific person registered as the tenant's admin at
  // onboarding time. Only that designated person, signing in themselves,
  // resolves this -- customer-side, not Raven's to fix.
  NOT_INITIAL_ADMIN: {
    message:
      "Your organization's ScopeAssistant setup is waiting on its designated administrator to sign in for the first time. If you're not sure who that is, check with whoever set up ScopeAssistant for your team.",
  },
  // The reader's own row exists but is_active is false -- only their own
  // company's ScopeAssistant admin (Settings -> Users) can re-enable it.
  ACCOUNT_DISABLED: {
    message: 'Your account has been disabled. Contact your ScopeAssistant administrator if you believe this is a mistake.',
  },
  // The whole tenant (not just this one user) has been suspended --
  // withAuthPooled.ts returns this instead of UNKNOWN_TENANT specifically
  // because the registry row still exists; "not registered" would wrongly
  // read as "our account was deleted." No link: re-enabling a suspended
  // tenant isn't something Raven's own marketing site helps with, and
  // (unlike UNKNOWN_TENANT's "are you interested in purchasing" case) this
  // reader is already a customer -- same no-link reasoning as
  // ACCOUNT_DISABLED, one level up (the whole org, not one user).
  TENANT_SUSPENDED: {
    message: "This organization's ScopeAssistant workspace has been suspended. Contact your account administrator.",
  },
}

// These two -- and only these two -- describe an infrastructure hiccup
// (the tenant registry or the caller's own tenant database being briefly
// unreachable), not a rejection of who the caller is. Retry is offered
// because trying again might genuinely succeed. Every other blocked state
// is permanent until someone acts, and offering Retry there is the exact
// bug this file exists to prevent: presenting a permanent authorization
// failure as a transient network error.
const RETRYABLE_CODES = new Set(['REGISTRY_UNAVAILABLE', 'DB_UNAVAILABLE'])

const RETRYABLE_MESSAGE = 'ScopeAssistant is temporarily unavailable. Try again in a moment.'

// An error code this file has never been told about is, by definition,
// not something the reader's own company's ScopeAssistant admin has any way to
// diagnose -- there's no button for them to click, no user record to fix.
// That points at Raven, the operator of the platform, not "your
// administrator." Deliberately NOT reworded to match UNKNOWN_TENANT's
// "confirm your credentials / interested in purchasing" phrasing, even
// though both name Raven: UNKNOWN_TENANT means no tenant exists at all --
// wrong credentials or a genuine prospect are the two real explanations.
// This message means something the app already resolved a tenant and a
// user for went wrong in a way nobody has named yet -- neither "wrong
// credentials" (they got past tenant/user resolution to get here) nor "are
// you interested in purchasing" (they're already a customer, mid-session)
// is a coherent thing to ask this reader. Keep this one describing what
// actually happened -- an unrecognized failure -- and let Raven Automation
// (still the right party, still linked) diagnose from there.
const GENERIC_MESSAGE = "We couldn't verify your access, and we don't recognize why. Contact Raven Automation if this continues."

// Every code withAuthPooled.ts/withPooledUserAuth.ts can emit that means
// "this session or tenant is no longer in a normal state" -- as opposed to
// an ordinary route's own business-rule rejection (FORBIDDEN for an
// insufficient role, NOT_FOUND for a missing resource, VALIDATION_ERROR,
// etc.), which must NOT blank the whole app into a global blocked state.
// Kept as an explicit, exhaustive list (not "anything 401/403") specifically
// because FORBIDDEN is also a 403 and is emitted for a perfectly ordinary,
// per-action rejection (requirePooledRole) -- status code alone can't tell
// the two apart, only the code can.
const AUTH_STATUS_CODES = new Set([
  'UNAUTHENTICATED',
  'UNKNOWN_TENANT',
  'TENANT_SUSPENDED',
  'REGISTRY_UNAVAILABLE',
  'EXTERNAL_TENANT',
  'TENANT_NOT_PROVISIONED',
  'NOT_INITIAL_ADMIN',
  'NO_INVITE_FOUND',
  'DB_UNAVAILABLE',
  'PENDING_APPROVAL',
  'ACCESS_DENIED',
  'ACCOUNT_DISABLED',
])

export interface AuthStatusActions {
  setPending: () => void
  setNoInvite: (email: string | null) => void
  setDenied: () => void
  setExternal: () => void
  setNotProvisioned: (info: { tenantSlug: string | null; canProvision: boolean; message: string }) => void
  setBlocked: (info: BlockedInfo) => void
}

// Shared by useCurrentUser.ts (the one query -- GET /auth/me -- whose whole
// job is establishing whether this session belongs here at all) and
// main.tsx's QueryCache-level onError (every other query). Previously only
// /auth/me's own rejection ever flipped authStatus away from
// 'authenticated' -- a tenant suspended mid-session left a signed-in user's
// authStatus stuck at 'authenticated' (that query stays cached, nothing
// re-triggers it) while every *other* page's own query independently 403'd
// and rendered its own local, inconsistent error. See quill-bug-log.md,
// "Suspending a tenant mid-session leaves a signed-in user in a
// partially-broken app."
//
// Returns false (and touches no state) when the error's code isn't one of
// the known auth-status codes above -- callers must not treat that as "safe
// to ignore" themselves: useCurrentUser.ts's own effect still has its own
// unconditional fallback for /auth/me specifically (an unrecognized code or
// a raw network failure on *that* query is still "we don't know what's
// going on, fail closed"), but the global QueryCache hook deliberately does
// NOT apply that same fallback to an arbitrary other query's unrelated
// error -- a transient network blip loading one dashboard widget must not
// blank the whole app.
export function applyKnownAuthStatus(err: unknown, actions: AuthStatusActions): boolean {
  if (!(err instanceof ApiError) || !err.code || !AUTH_STATUS_CODES.has(err.code)) return false

  if (err.code === 'PENDING_APPROVAL') actions.setPending()
  else if (err.code === 'NO_INVITE_FOUND') actions.setNoInvite(err.presentedEmail ?? null)
  else if (err.code === 'ACCESS_DENIED') actions.setDenied()
  else if (err.code === 'EXTERNAL_TENANT') actions.setExternal()
  else if (err.code === 'TENANT_NOT_PROVISIONED') {
    actions.setNotProvisioned({ tenantSlug: err.tenantSlug ?? null, canProvision: err.canProvision ?? false, message: err.message })
  } else actions.setBlocked(resolveBlockedInfo(err))

  return true
}

export function resolveBlockedInfo(err: unknown): BlockedInfo {
  if (err instanceof ApiError) {
    if (err.code && RETRYABLE_CODES.has(err.code)) {
      return { code: err.code, message: RETRYABLE_MESSAGE, retryable: true }
    }
    const known = err.code ? KNOWN_MESSAGES[err.code] : undefined
    // An unrecognized code gets the same Raven link the generic message's
    // audience implies -- there's no KNOWN_MESSAGES entry to carry it, so
    // it's supplied directly here rather than left off by omission. The
    // link, when present, comes from `known.link` (may itself be
    // undefined, e.g. NOT_INITIAL_ADMIN/ACCOUNT_DISABLED) -- built with a
    // conditional spread rather than `link: x | undefined` so an absent
    // link is an absent property, not an explicit undefined value
    // (exactOptionalPropertyTypes).
    const link = known ? known.link : RAVEN_LINK
    return {
      code: err.code ?? null,
      message: known?.message ?? GENERIC_MESSAGE,
      retryable: false,
      ...(link ? { link } : {}),
    }
  }
  // Not an ApiError -- e.g. a raw network failure (fetch rejected before a
  // response was ever received). Genuinely transient in the same sense as
  // the two codes above, so it gets the same retryable message.
  return { code: null, message: RETRYABLE_MESSAGE, retryable: true }
}
